import { Component, inject } from "@angular/core";
import { MoiExportConfigModel } from "@app/export/shared/models/moi-export-config-model";
import { MoiDataService } from "@app/moi/services/moi-data.service";
import {
    GenericListField,
    GenericListFieldType,
} from "@app/shared/models/GenericList/GenericListField";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";
import { nameof } from "@methods/jeffs-toolkit";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { map } from "rxjs/operators";
import { ExportBase } from "../export-base";
import { ExportType } from "./../../../../common/enums/ExportType";
import { IdNameCombination } from "./../../../../common/models/generic/IdNameCombination";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "export-bim",
    templateUrl: "./export-bim.component.html",
    styleUrls: ["./export-bim.component.scss"],
    providers: [provideTranslocoScope("export")],
    imports: [TranslocoPipe]
})
export class ExportBimComponent extends ExportBase {
    private _ds = inject(MoiDataService);
    permService = inject(PermissionsService);
    private cerrixPrompt = inject(CerrixPromptService);
    private sdDataService = inject(StandingdataDataService);
    private transloco = inject(TranslocoService);

    public isBimAdmin;

    constructor() {
        super();

        const permService = this.permService;
        this.isBimAdmin = permService.permissions.IsBimAdmin;
    }

    moiExport() {
        var fields = this.getFields([
            this.fieldName((x) => x.moiTypes),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.includeDeletedandClosedmois),
        ]);

        this.showExportPopup(this.transloco.translate("export.bim.moisExportTitle"), null, fields, ExportType.Mois);
    }

    moiSummaryExport() {
        var fields = this.getFields([
            this.fieldName((x) => x.moiTypes),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.includeDeletedandClosedmois),
        ]);

        this.showExportPopup(this.transloco.translate("export.bim.moiSummaryExportTitle"), null, fields, ExportType.MoiSummary);
    }

    moiCommentsExport() {
        var fields = this.getFields([
            this.fieldName((x) => x.moiTypes),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.includeDeletedandClosedmois),
        ]);

        this.showExportPopup(this.transloco.translate("export.bim.moiAndCommentsExportTitle"), null, fields, ExportType.MoiComments);
    }

    findingReportAssessmentExport() {
        var fields = this.getFields([this.fieldName((x) => x.reportTypes)]);

        this.showExportPopup(
            this.transloco.translate("export.bim.findingReportsAssessmentsTitle"),
            null,
            fields,
            ExportType.FindingReportsAndAssessments
        );
    }

    findingReportInformationExport() {
        var fields = this.getFields([
            this.fieldName((x) => x.organizations),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.findingReports),
        ]);

        this.showExportPopup(
            this.transloco.translate("export.bim.findingReportInformationTitle"),
            null,
            fields,
            ExportType.FindingReportInformation
        );
    }

    showExportPopup(
        title: string,
        message: string,
        fields: GenericListField[],
        exportype: ExportType
    ): void {
        this.cerrixPrompt
            .prompt({
                height: "500px",
                data: {
                    title: title,
                    message: message,
                    fields: fields,

                    confirmButton: {
                        text: this.transloco.translate("export.downloadButton"),
                        icon: "fad fa-cloud-download",
                    },
                },
            })
            .toPromise()
            .then((result: MoiExportConfigModel) => {
                if (!result) {
                    return;
                }

                const extraString = this.toRequestQuery(result);
                this.download(exportype, extraString);
            });
    }

    private fieldName(nameFunction: (obj: MoiExportConfigModel) => any) {
        return nameof<MoiExportConfigModel>(nameFunction);
    }

    private getFields(fieldNames: string[]): GenericListField[] {
        const requested = (field: string) => {
            return fieldNames.indexOf(field) >= 0;
        };

        var fields: GenericListField[] = [];

        if (requested(this.fieldName((x) => x.organizations))) {
            fields.push({
                prettyName: this.transloco.translate("export.organizationsField"),
                fieldName: this.fieldName((x) => x.organizations),
                fieldType: GenericListFieldType.MultiTree,
                required: true,
                selfInit: true,
                getDataMethod: () => {
                    return this.sdDataService.getOrganizations(ModuleType.BIM);
                },
            });
        }

        if (requested(this.fieldName((x) => x.moiTypes))) {
            fields.push({
                prettyName: this.transloco.translate("export.bim.moiTypesField"),
                fieldName: this.fieldName((x) => x.moiTypes),
                fieldType: GenericListFieldType.MultiSelect,
                placeholder: this.transloco.translate("export.noFilter"),
                selfInit: true,
                getDataMethod: () => {
                    return this._ds
                        .getExportableMoiTypes()
                        .pipe(map((x) => x.map((i) => new IdNameCombination(i.value, i.label))));
                },
            });
        }

        if (requested(this.fieldName((x) => x.reportTypes))) {
            fields.push({
                prettyName: this.transloco.translate("export.bim.reportTypesField"),
                fieldName: this.fieldName((x) => x.reportTypes),
                fieldType: GenericListFieldType.MultiSelect,
                placeholder: this.transloco.translate("export.noFilter"),
                selfInit: true,
                getDataMethod: () => {
                    return this._ds
                        .getExportableReportTypes()
                        .pipe(map((x) => x.map((i) => new IdNameCombination(i.value, i.label))));
                },
            });
        }

        if (requested(this.fieldName((x) => x.findingReports))) {
            fields.push({
                prettyName: this.transloco.translate("export.bim.findingReportsField"),
                fieldName: this.fieldName((x) => x.findingReports),
                fieldType: GenericListFieldType.MultiSelect,
                placeholder: this.transloco.translate("export.noFilter"),
                selfInit: true,
                getDataMethod: () => {
                    return this._ds
                        .getExportableFindingReports()
                        .pipe(map((x) => x.map((i) => new IdNameCombination(i.value, i.label))));
                },
            });
        }

        if (requested(this.fieldName((x) => x.includeDeletedandClosedmois))) {
            fields.push({
                prettyName: this.transloco.translate("export.bim.includeClosedMoisField"),
                fieldName: this.fieldName((x) => x.includeDeletedandClosedmois),
                fieldType: GenericListFieldType.CheckBox,
                hideFillers: true,
                selfInit: true,
            });
        }

        return fields;
    }

    private toRequestQuery(model: MoiExportConfigModel): string {
        const stringSections: string[] = [];
        if (model.includeDeletedandClosedmois) {
            stringSections.push("includedeletedandclosedmois=true");
        }

        if (model.organizations && model.organizations.length > 0) {
            stringSections.push("organizations=" + model.organizations.join(";"));
        }

        if (model.moiTypes && model.moiTypes.length > 0) {
            stringSections.push("moiTypes=" + model.moiTypes.join(";"));
        }

        if (model.reportTypes && model.reportTypes.length > 0) {
            stringSections.push("reportTypes=" + model.reportTypes.join(";"));
        }

        if (model.findingReports && model.findingReports.length > 0) {
            stringSections.push("findingReports=" + model.findingReports.join(";"));
        }

        const extraString = stringSections.length > 0 ? stringSections.join("&") : "";
        return extraString;
    }
}
